import cn from 'classnames';
import { Card, Form } from 'react-bootstrap';
import { Dropdown } from '@Components/Dropdown';
import { Img } from '@Components/Img';
import { Size } from '@Components/designConstants';
import { getCreativeThumbnail } from '@Components/MetricsTable/metrics-table-utils';
import { AudioCompanionOption, DisabledReasonWrapper } from '@Components/CreativeAllocation';
import {
  CurrentCompanion,
  REMOVE_COMPANION,
  WorkspaceMetadata
} from './streaming-creatives-constants';

const FT_LABELS = ['ft', 'flashtalking'];

interface EditableCardProps {
  name: string;
  isci?: string;
  creative: string;
  length?: number;
  value: number;
  color?: string;
  onChange: (name: string, value: number) => void;
  notLive?: boolean;
  company?: string;
  audioCompanion?: string;
  currentCompanion?: CurrentCompanion;
  audioCompanionOptions?: AudioCompanionOption[];
  clickthroughURL?: string;
  // Only reachable for Flashtalking ad servers; callers with no ad server (e.g. linear
  // creative allocation) never render the metadata form and can omit both.
  onMetadataChange?: (name: string, metadata: WorkspaceMetadata) => void;
  adServer?: string;
  disabled?: boolean;
  disabledReason?: string;
}

export const EditableCard = ({
  name,
  isci,
  creative,
  length,
  value,
  color,
  onChange,
  notLive,
  company = '',
  audioCompanion,
  currentCompanion,
  audioCompanionOptions = [],
  clickthroughURL,
  onMetadataChange = () => {},
  adServer = '',
  disabled = false,
  disabledReason
}: EditableCardProps) => {
  const CARD_WIDTH = 190;
  const STRIPE_WIDTH = 5;
  const TEXT_HEIGHT = 60;
  const FORM_HEIGHT = 55;
  const WARNING_HEIGHT = 32;
  const companionOptions: { label: string; value: string }[] = [];
  if (FT_LABELS.includes(adServer)) {
    for (const { adid, title } of audioCompanionOptions) {
      companionOptions.push({ label: `${adid} (${title})`, value: adid });
    }
  }
  const hasCompanions = companionOptions.length > 0;
  const canRemove = Boolean(currentCompanion?.value || currentCompanion?.mixed || audioCompanion);
  if (hasCompanions && canRemove) {
    companionOptions.unshift({ label: 'No companion (remove)', value: REMOVE_COMPANION });
  }
  const companionValue = audioCompanion ?? currentCompanion?.value ?? '';
  const isMixedUntouched = Boolean(currentCompanion?.mixed) && audioCompanion === undefined;
  const showMixedWarning = Boolean(currentCompanion?.mixed) && audioCompanion !== undefined;
  const thumbHeight = ((CARD_WIDTH - STRIPE_WIDTH) * 9) / 16;
  let formRows = 1;
  if (FT_LABELS.includes(adServer)) {
    formRows = hasCompanions ? 3 : 2;
  }
  return (
    <DisabledReasonWrapper
      reason={disabled ? disabledReason : undefined}
      className='creative-card-tooltip'
    >
      <Card
        className={cn('creative-card-container', { 'not-live': notLive, disabled })}
        style={{
          height:
            thumbHeight +
            TEXT_HEIGHT +
            FORM_HEIGHT * formRows +
            (hasCompanions && showMixedWarning ? WARNING_HEIGHT : 0),
          width: CARD_WIDTH
        }}
      >
        <div className='stripe' style={{ width: STRIPE_WIDTH, backgroundColor: color }} />
        <div className='creative-card'>
          <div className='creative-content'>
            <div
              className='creative-img-box'
              style={{ height: thumbHeight, minHeight: thumbHeight, maxHeight: thumbHeight }}
            >
              <Img
                src={getCreativeThumbnail(company, isci ?? '')}
                className='creative-img'
                unloader={<div className='no-img'>Thumbnail not found</div>}
              />
            </div>
            <div className='isci'>{name}</div>
            <div className='subtitle' title={`${creative} (${length}s)`}>
              {creative} <span className='length'>({length}s)</span>
            </div>
          </div>
          <div className='alloc-input'>
            <Form.Control
              type='number'
              disabled={disabled}
              value={`${value}`}
              onChange={e => {
                const parsed = parseInt(e.target.value, 10);
                onChange(name, Number.isNaN(parsed) ? 0 : parsed);
              }}
            />
          </div>
          {FT_LABELS.includes(adServer) && (
            <div className='clickthrough-url-input'>
              <div className='label'>Click-Through URL:</div>
              <Form.Control
                type='text'
                disabled={disabled}
                value={clickthroughURL || ''}
                onChange={e =>
                  onMetadataChange(name, { audioCompanion, clickthroughURL: e.target.value })
                }
              />
            </div>
          )}
          {hasCompanions && FT_LABELS.includes(adServer) && (
            <div className='companion-banner-input'>
              <div className='label'>Audio Companion Banner:</div>
              <Dropdown
                searchable
                className='companion-dropdown'
                size={Size.SM}
                disabled={disabled}
                placeholder={isMixedUntouched ? 'Mixed - unchanged' : 'Select Companion...'}
                value={companionValue}
                options={companionOptions}
                clearable={Boolean(companionValue) && companionValue !== REMOVE_COMPANION}
                onChange={newVal =>
                  onMetadataChange(name, {
                    audioCompanion:
                      newVal || (audioCompanion === undefined ? REMOVE_COMPANION : undefined),
                    clickthroughURL
                  })
                }
              />
              {showMixedWarning && (
                <div className='companion-warning'>
                  {(currentCompanion?.bannerCount ?? 0) > 1
                    ? `Replaces ${currentCompanion?.bannerCount} different current banners on the selected placements.`
                    : 'Replaces the current banner on some of the selected placements.'}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    </DisabledReasonWrapper>
  );
};

interface StaticCardProps {
  name: string;
  isci?: string;
  creative: string;
  length?: number;
  value: number;
  color?: string;
  isLive?: boolean;
  company?: string;
}

export const StaticCard = ({
  name,
  isci,
  creative,
  length,
  value,
  color,
  isLive,
  company = ''
}: StaticCardProps) => {
  const CARD_WIDTH = 170;
  const STRIPE_WIDTH = 5;
  const TEXT_HEIGHT = 70;
  const FORM_HEIGHT = 40;
  const thumbHeight = ((CARD_WIDTH - STRIPE_WIDTH) * 9) / 16;
  return (
    <Card
      className='static-card-container'
      style={{
        height: thumbHeight + TEXT_HEIGHT + FORM_HEIGHT,
        width: CARD_WIDTH,
        opacity: isLive ? 1 : 0.5
      }}
    >
      <div className='stripe' style={{ width: STRIPE_WIDTH, backgroundColor: color }} />
      <div className='creative-card'>
        <div className='creative-content'>
          <div
            className='creative-img-box'
            style={{ height: thumbHeight, minHeight: thumbHeight, maxHeight: thumbHeight }}
          >
            <Img
              src={getCreativeThumbnail(company, isci ?? '')}
              className='creative-img'
              unloader={<div className='no-img'>Thumbnail not found</div>}
            />
          </div>
          <div className='isci'>{name}</div>
          <div className='subtitle' title={`${creative} (${length}s)`}>
            {creative} <span className='length'>({length}s)</span>
          </div>
        </div>
        <div className='alloc'>{value}%</div>
      </div>
    </Card>
  );
};
